import type {
  BasisComponent,
  CalculationInput,
  CalculationResult,
  ExpenseLine,
  FeeCategory,
  FeeLine,
  FeeProfile,
  FeeRule,
  ManualFeeOverride,
  TaxCollector,
} from "@netkeep/fee-types";
import {
  type Dec,
  FeeEngineError,
  HUNDRED,
  ZERO,
  moneyString,
  parseAmount,
  percentString,
  roundMoney,
} from "./money";
import { type BasisAmounts, computeFeeLine } from "./rules";
import { solveSalePrice } from "./solve";

/** Bumped whenever a formula changes so stored results can be traced. */
export const FORMULA_VERSION = "1.0.0";

const DEFAULT_MANUAL_BASIS: BasisComponent[] = ["ITEM_PRICE", "BUYER_SHIPPING"];

export interface CalculateOptions {
  /** Compute the break-even price (runs the solver). Defaults to true. */
  includeBreakEven?: boolean;
}

interface PlannedRule {
  rule: FeeRule;
  source: "PROFILE" | "MANUAL";
}

function hasOverride(o: ManualFeeOverride | undefined): o is ManualFeeOverride {
  return o != null && (o.percent != null || o.fixedAmount != null);
}

/**
 * Replaces every profile rule of `category` with a single manual rule.
 * Replacing (rather than adding) is what prevents a manually entered
 * marketplace fee from being charged on top of the preset one.
 */
function applyOverride(
  planned: PlannedRule[],
  category: FeeCategory,
  override: ManualFeeOverride,
  code: string,
  label: string,
): PlannedRule[] {
  const replaced = planned.filter((p) => p.rule.category === category);
  const basis = override.basis ?? replaced[0]?.rule.basis ?? DEFAULT_MANUAL_BASIS;
  const manual: FeeRule = { code, label, category, basis: [...basis] };
  if (override.percent != null) manual.percent = override.percent;
  if (override.fixedAmount != null) manual.fixedAmount = override.fixedAmount;
  return [...planned.filter((p) => p.rule.category !== category), { rule: manual, source: "MANUAL" }];
}

function computeSalesTax(input: CalculationInput, price: Dec, shipping: Dec, currency: string): Dec {
  const tax = input.salesTax;
  switch (tax.mode) {
    case "NONE":
      return ZERO;
    case "AMOUNT":
      return roundMoney(parseAmount(tax.amount, "salesTax.amount"), currency);
    case "RATE": {
      const rate = parseAmount(tax.ratePercent, "salesTax.ratePercent");
      if (rate.greaterThan(HUNDRED)) throw new FeeEngineError("salesTax.ratePercent cannot exceed 100", "salesTax");
      const base = tax.includeShipping ? price.plus(shipping) : price;
      return roundMoney(base.times(rate).dividedBy(HUNDRED), currency);
    }
    default:
      throw new FeeEngineError("Unknown sales tax mode", "salesTax");
  }
}

function ratio(numerator: Dec, denominator: Dec): string | null {
  return denominator.isZero() ? null : percentString(numerator.dividedBy(denominator).times(HUNDRED));
}

/**
 * Calculates an itemized sale breakdown.
 *
 * Formulas (all money rounded to the currency minor unit, ROUND_HALF_UP):
 *   sellerRevenue = salePrice + shippingCharged                (tax excluded)
 *   buyerTotal    = sellerRevenue + salesTax
 *   totalFees     = Σ fee lines (each fee rounded individually)
 *   totalExpenses = shippingCost + packaging + other + subscriptionOverhead
 *   netPayout     = sellerRevenue − totalFees (+ salesTax when the SELLER collects tax)
 *   taxLiability  = salesTax when the SELLER collects it, else 0
 *   netProfit     = sellerRevenue − totalFees − totalExpenses − itemCost
 *   margin %      = netProfit ÷ sellerRevenue × 100
 *   ROI %         = netProfit ÷ itemCost × 100
 *
 * Sales tax is never seller revenue and never a seller expense. It only
 * influences fees through rules whose basis includes SALES_TAX.
 *
 * @param profile Platform fee profile, or null for a fully manual (custom) calculation.
 */
export function calculate(
  input: CalculationInput,
  profile: FeeProfile | null,
  options: CalculateOptions = {},
): CalculationResult {
  const currency = input.currency.toUpperCase();
  if (profile && profile.currency.toUpperCase() !== currency) {
    throw new FeeEngineError(
      `Input currency ${currency} does not match fee profile currency ${profile.currency}`,
      "currency",
    );
  }
  const warnings: string[] = [];

  const price = roundMoney(parseAmount(input.salePrice, "salePrice"), currency);
  const shipping = roundMoney(parseAmount(input.shippingCharged, "shippingCharged"), currency);
  const itemCost = roundMoney(parseAmount(input.itemCost, "itemCost"), currency);

  const taxCollectedBy: TaxCollector = input.taxCollectedBy ?? profile?.taxCollectedBy ?? "NONE";
  let salesTax = computeSalesTax(input, price, shipping, currency);
  if (taxCollectedBy === "NONE" && !salesTax.isZero()) {
    warnings.push("Sales tax was entered but tax collection is set to none, so it was ignored.");
    salesTax = ZERO;
  }

  // Each rule must have a unique code inside a profile.
  const codes = new Set<string>();
  for (const rule of profile?.rules ?? []) {
    if (codes.has(rule.code)) throw new FeeEngineError(`Duplicate fee rule code ${rule.code}`, rule.code);
    codes.add(rule.code);
  }

  let planned: PlannedRule[] = (profile?.rules ?? []).map((rule) => ({ rule, source: "PROFILE" as const }));
  const overrides = input.overrides ?? {};
  if (hasOverride(overrides.marketplace)) {
    planned = applyOverride(planned, "MARKETPLACE", overrides.marketplace, "MANUAL_MARKETPLACE", "Marketplace fee");
  }
  if (hasOverride(overrides.paymentProcessing)) {
    const pp = overrides.paymentProcessing;
    const isNonZero = [pp.percent, pp.fixedAmount].some((v) => v != null && !parseAmount(v, "override").isZero());
    if (profile?.paymentProcessingIncluded && isNonZero) {
      warnings.push(
        "This platform's fee already includes payment processing. A separate payment fee was added because you entered one manually.",
      );
    }
    planned = applyOverride(planned, "PAYMENT_PROCESSING", pp, "MANUAL_PAYMENT", "Payment processing fee");
  }

  const promotedPercent = parseAmount(input.promotedListingPercent, "promotedListingPercent");
  if (promotedPercent.greaterThan(HUNDRED)) {
    throw new FeeEngineError("promotedListingPercent cannot exceed 100", "promotedListingPercent");
  }
  if (promotedPercent.greaterThan(0)) {
    planned = planned.filter((p) => p.rule.category !== "PROMOTED_LISTING");
    planned.push({
      rule: {
        code: "PROMOTED_LISTING",
        label: "Promoted listing fee",
        category: "PROMOTED_LISTING",
        basis: [...(profile?.promotedListingBasis ?? DEFAULT_MANUAL_BASIS)],
        percent: promotedPercent.toString(),
      },
      source: "MANUAL",
    });
  }

  const amounts: BasisAmounts = { ITEM_PRICE: price, BUYER_SHIPPING: shipping, SALES_TAX: salesTax };
  const fees: FeeLine[] = planned.map(({ rule, source }) => computeFeeLine(rule, amounts, currency, source));
  const totalFees = fees.reduce((sum, f) => sum.plus(f.amount), ZERO);

  const expenseInputs: Array<[ExpenseLine["code"], string, string | undefined]> = [
    ["SHIPPING_COST", "Shipping label / postage", input.shippingCost],
    ["PACKAGING", "Packaging", input.packagingCost],
    ["OTHER", "Other expenses", input.otherExpenses],
    ["SUBSCRIPTION", "Subscription overhead", input.subscriptionOverhead],
  ];
  const expenses: ExpenseLine[] = expenseInputs.map(([code, label, value]) => ({
    code,
    label,
    amount: moneyString(roundMoney(parseAmount(value, code), currency), currency),
  }));
  const totalExpenses = expenses.reduce((sum, e) => sum.plus(e.amount), ZERO);

  const sellerRevenue = price.plus(shipping);
  const buyerTotal = sellerRevenue.plus(salesTax);
  const taxLiability = taxCollectedBy === "SELLER" ? salesTax : ZERO;
  const netPayout = sellerRevenue.minus(totalFees).plus(taxLiability);
  const netProfit = sellerRevenue.minus(totalFees).minus(totalExpenses).minus(itemCost);

  if (netPayout.isNegative()) {
    warnings.push("Fees exceed the amount received, so the platform may bill you for the difference.");
  }
  if (input.salesTax.mode === "AMOUNT" && !salesTax.isZero()) {
    warnings.push("Sales tax was entered as a fixed amount, so break-even assumes the tax does not change with price.");
  }

  let breakEvenPrice: string | null = null;
  if (options.includeBreakEven !== false) {
    const solved = solveSalePrice(input, profile, "0");
    breakEvenPrice = solved == null ? null : solved;
  }

  return {
    currency,
    formulaVersion: FORMULA_VERSION,
    profileId: profile?.id ?? null,
    taxCollectedBy,
    revenue: {
      itemPrice: moneyString(price, currency),
      shippingCharged: moneyString(shipping, currency),
      sellerRevenue: moneyString(sellerRevenue, currency),
      salesTax: moneyString(salesTax, currency),
      buyerTotal: moneyString(buyerTotal, currency),
    },
    fees,
    totalFees: moneyString(totalFees, currency),
    expenses,
    totalExpenses: moneyString(totalExpenses, currency),
    itemCost: moneyString(itemCost, currency),
    netPayout: moneyString(netPayout, currency),
    taxLiability: moneyString(taxLiability, currency),
    netProfit: moneyString(netProfit, currency),
    profitMarginPercent: ratio(netProfit, sellerRevenue),
    roiPercent: ratio(netProfit, itemCost),
    effectiveFeePercent: ratio(totalFees, sellerRevenue),
    breakEvenPrice,
    warnings,
  };
}
